import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'

import type { DatabaseSearchResult } from '@/models/DatabaseSearch'
import { exportFileName, exportRow } from './selectionsExport'

const NAVY = '#1C3045'
const MUTED = '#666'

function entry(row: DatabaseSearchResult): Content {
  const [funder, opportunity, link, type, location, areas, timing, tags] =
    exportRow(row)
  const line = (label: string, value: Content) => [
    { text: label, color: MUTED },
    value,
  ]
  return {
    // Keeps each opportunity on one page.
    unbreakable: true,
    margin: [0, 0, 0, 14],
    stack: [
      { text: funder, bold: true, fontSize: 12, color: NAVY },
      { text: opportunity, fontSize: 11, margin: [0, 1, 0, 5] },
      {
        table: {
          widths: [90, '*'],
          body: [
            line(
              'Link',
              link
                ? { text: link, link, color: NAVY, decoration: 'underline' }
                : 'None provided',
            ),
            line('Application type', type),
            line('Location', areas ? `${location}: ${areas}` : location),
            line('Timing', timing),
            line('Tags', tags || '—'),
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingTop: () => 1,
          paddingBottom: () => 1,
        },
        fontSize: 9.5,
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 8,
            x2: 515,
            y2: 8,
            lineWidth: 0.5,
            lineColor: '#d5d9df',
          },
        ],
      },
    ],
  }
}

/** A4 portrait, one entry per selection in the order ticked. */
export function selectionsPdfDefinition(
  rows: DatabaseSearchResult[],
  now: Date = new Date(),
): TDocumentDefinitions {
  const date = now.toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const count = `${rows.length} ${rows.length === 1 ? 'opportunity' : 'opportunities'}`
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [40, 48, 40, 48],
    info: { title: 'Selected funding opportunities' },
    // pdfmake's bundled Roboto carries macrons.
    defaultStyle: { font: 'Roboto', fontSize: 10, lineHeight: 1.15 },
    content: [
      { text: 'Selected funding opportunities', fontSize: 17, bold: true, color: NAVY },
      {
        text: `From Fundsorter's Browse Database · ${date} · ${count}`,
        color: MUTED,
        margin: [0, 3, 0, 16],
      },
      ...rows.map(entry),
    ],
    footer: (page, pages) => ({
      text: `Page ${page} of ${pages}`,
      alignment: 'right',
      fontSize: 8,
      color: '#888',
      margin: [40, 16, 40, 0],
    }),
  }
}

let pdfMakeReady: Promise<typeof import('pdfmake/build/pdfmake')> | null = null

// Loaded on first use, so the library and its fonts stay out of the main bundle.
function loadPdfMake() {
  pdfMakeReady ??= Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ])
    .then(([pdfMake, fonts]) => {
      const lib = (pdfMake.default ?? pdfMake) as typeof import('pdfmake/build/pdfmake')
      lib.addVirtualFileSystem(fonts.default ?? fonts)
      return lib
    })
    .catch((error) => {
      // A failed chunk load can succeed on the next click.
      pdfMakeReady = null
      throw error
    })
  return pdfMakeReady
}

export async function downloadSelectionsPdf(rows: DatabaseSearchResult[]): Promise<void> {
  const pdfMake = await loadPdfMake()
  await pdfMake.createPdf(selectionsPdfDefinition(rows)).download(exportFileName('pdf'))
}
